/** @format */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  InfoToast,
  PostIdea as PostIdeaComponent,
  Transition,
  theme,
  useGetLocation,
  useIsMobile,
} from "senf-atomic-design-system";
import IdeaPin from "senf-atomic-design-system/src/assets/icons/IdeaPin";
import DrawingFeature from "senf-atomic-design-system/src/components/atoms/map/DrawingFeature";
import NoLocationMapOverlay from "./NoLocationMapOverlay";

const PostIdeaWrapper = ({
  addIdeaOptions,
  handle,
  statefulMap,
  project,
  ideaMap,
  geoDataType,
  formik,
  setLocation,
  pathname,
  handleSubmit,
  setGeoDataType,
  index,
  setIndex,
  isSubmitting,
  ideaId,
  variant,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [featureId, setFeatureId] = useState<string | null>(null);
  const [drawMode, setDrawMode] = useState<{
    mode: string | null;
    drawType: string | null;
  }>({ mode: null, drawType: null });

  const { isOut } = useGetLocation(
    statefulMap,
    project,
    ideaMap,
    geoDataType,
    formik,
    setLocation,
  );

  return (
    <React.Fragment>
      <Transition
        show={geoDataType === "location"}
        from={{ x: 0, y: -50, scale: 1 }}
        leave={{ x: 0, y: -50, scale: 1 }}
        enter={{ x: 0, y: 0, scale: 1 }}
      >
        <Box
          width={isMobile ? "100vw" : "calc(100vw - 430px)"}
          position="fixed"
          height={isMobile ? "calc(100vh - 150px)" : "100vh"}
          bottom={0}
          justifyContent="center"
          alignItems="center"
          zIndex={2}
          pointerEvents="none"
          left={isMobile || document.dir === "rtl" ? "0" : "430px"}
        >
          <IdeaPin
            color={
              (() =>
                ideaMap?.customTopics.reduce((acc, { value, label, color }) => {
                  return { ...acc, [value]: { label, color } };
                }, {}))()?.[formik?.values?.topic]?.color ||
              theme.colors.primary.primary100
            }
            transform="scale(1.5) translateY(-10px)"
          />
        </Box>
      </Transition>

      {geoDataType === "noLocation" && (
        <NoLocationMapOverlay
          statefulMap={statefulMap}
          ideaMap={ideaMap}
          formik={formik}
        />
      )}

      {(geoDataType === "path" || geoDataType === "area") && (
        <React.Fragment>
          <InfoToast
            className={
              isMobile
                ? "left-2.5 top-2.5 right-2.5 w-[calc(100vw-20px)]"
                : "left-[450px] top-2.5"
            }
            text={
              geoDataType === "path"
                ? t("map:drawInfo.path")
                : t("map:drawInfo.area")
            }
            closeable={true}
            position="fixed"
            withShadow={true}
          />

          <Box
            position="fixed"
            zIndex={99}
            bottom="20px"
            left={isMobile ? "0px" : "430px"}
            width={isMobile ? "100%" : "calc(100vw - 430px)"}
            pointerEvents="none"
          >
            <DrawingFeature
              mapStyle={ideaMap?.mapStyle}
              mapType={drawMode as any}
              statefulMap={statefulMap}
              handleSaveDrawn={(polygon) => {
                formik.setFieldValue("geoData", polygon);
              }}
              featureCollection={formik.values.geoData}
              featureId={featureId}
              setFeatureId={setFeatureId}
              openDescription={false}
              inMainMap={true}
              setDrawMode={setDrawMode}
              initialDrawType={geoDataType}
            />
          </Box>
        </React.Fragment>
      )}

      <PostIdeaComponent
        ideaMap={ideaMap}
        addIdeaOptions={addIdeaOptions}
        addIdeaOpen={
          pathname.indexOf("addIdea") > -1 || pathname.indexOf("editIdea") > -1
        }
        statefulMap={statefulMap}
        formik={formik}
        editIdea={ideaId}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        loading={false}
        hide={false}
        handle={handle}
        project={project}
        index={index}
        setIndex={setIndex}
        geoDataType={geoDataType}
        setGeoDataType={setGeoDataType}
        bbox={ideaMap?.bbox}
        isOut={isOut}
        variant={variant}
      />
    </React.Fragment>
  );
};

export default PostIdeaWrapper;
